// apps/web/src/hooks/use-ao-vivo.ts — Fase 17.8, Plano 12 (D-13; UI-SPEC U-25).
// Recarga da home da aba ativa: (1) um `painel_invalidar` que inclui o perfil da aba agenda a recarga com debounce de 5 s
// (origem 'ao-vivo': rolo e brilho); (2) a cada ~2 min recarrega em silêncio, só com a aba em primeiro plano, e ao voltar ao
// primeiro plano depois de mais de 2 min recarrega na hora; (3) um 401 (sessão expirada: o polling e o SSE não renovam a sessão)
// para tudo e leva ao login. Cada recarga bem-sucedida registra a hora para o indicador "Atualizado há Xs" da topbar.
import { useEffect, useRef } from 'react';
import type { PerfilHome } from '@pieltec/contracts';
import { useCanalAoVivo } from '../components/shell/ao-vivo-provider';
import { criarDebounce } from '../lib/kanban/sse';
import { DEBOUNCE_REFETCH_MS, RECARGA_VISIVEL_MS } from '../lib/painel/home/ao-vivo';
import type { ResultadoHome } from '../lib/painel/home/carga-home';

/** Origem de uma recarga: 'ao-vivo' (evento do stream: rola e brilha) ou 'silenciosa' (timer e retorno da aba: sem animação). */
export type OrigemRecarga = 'ao-vivo' | 'silenciosa';

export interface OpcoesUseAoVivo {
  /** Perfil da aba ativa; `null` quando não há home recarregável (nada é agendado). */
  readonly perfilAtivo: PerfilHome | null;
  /** Recarrega a home do perfil ativo e devolve o resultado (o 401 chega como `nao-autenticado`). */
  readonly recarregar: (origem: OrigemRecarga) => Promise<ResultadoHome<unknown>>;
}

const ENDERECO_LOGIN = '/login';

export function useAoVivo({ perfilAtivo, recarregar }: OpcoesUseAoVivo): void {
  const { assinar, registrarAtualizacao } = useCanalAoVivo();
  const recarregarRef = useRef(recarregar);
  const registrarRef = useRef(registrarAtualizacao);
  // Última tentativa por perfil (sobrevive à troca de aba): decide se uma aba já carregada precisa de recarga ao voltar.
  const ultimaTentativa = useRef<Map<PerfilHome, number>>(new Map());
  const emVoo = useRef(false);
  const pendenteAoVivo = useRef(false);
  const parado = useRef(false);

  useEffect(() => {
    recarregarRef.current = recarregar;
    registrarRef.current = registrarAtualizacao;
  }, [recarregar, registrarAtualizacao]);

  useEffect(() => {
    if (perfilAtivo === null || parado.current) return undefined;
    const perfil = perfilAtivo;
    let vivo = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const visivel = (): boolean => document.visibilityState === 'visible';
    const decorrido = (): number => Date.now() - (ultimaTentativa.current.get(perfil) ?? Date.now());

    const agendar = (): void => {
      clearTimeout(timer);
      timer = setTimeout(aoVencer, Math.max(0, RECARGA_VISIVEL_MS - decorrido()));
    };

    async function executar(origem: OrigemRecarga): Promise<void> {
      if (!vivo || parado.current) return;
      if (emVoo.current) {
        if (origem === 'ao-vivo') pendenteAoVivo.current = true;
        return;
      }
      emVoo.current = true;
      ultimaTentativa.current.set(perfil, Date.now());
      let resultado: ResultadoHome<unknown>;
      try {
        resultado = await recarregarRef.current(origem);
      } catch {
        resultado = { tipo: 'erro' };
      } finally {
        emVoo.current = false;
      }
      if (resultado.tipo === 'nao-autenticado') {
        // Sessão expirada: nada mais é agendado e a pessoa volta ao login.
        parado.current = true;
        clearTimeout(timer);
        window.location.assign(ENDERECO_LOGIN);
        return;
      }
      if (!vivo) return;
      if (resultado.tipo === 'sucesso') registrarRef.current(new Date());
      if (pendenteAoVivo.current) {
        pendenteAoVivo.current = false;
        void executar('ao-vivo');
        return;
      }
      agendar();
    }

    // Venceu o prazo de ~2 min: recarrega só com a aba visível; oculta, espera o retorno ao primeiro plano.
    function aoVencer(): void {
      if (visivel()) void executar('silenciosa');
    }

    const aoMudarVisibilidade = (): void => {
      if (!visivel() || parado.current) return;
      if (decorrido() >= RECARGA_VISIVEL_MS) void executar('silenciosa');
      else agendar();
    };

    const debounced = criarDebounce(() => {
      if (vivo) void executar('ao-vivo');
    }, DEBOUNCE_REFETCH_MS);

    const cancelarAssinatura = assinar((evento) => {
      if (evento.tipo === 'painel_invalidar' && evento.perfis.includes(perfil)) debounced();
    });

    document.addEventListener('visibilitychange', aoMudarVisibilidade);

    if (!ultimaTentativa.current.has(perfil)) {
      // Primeira vez nesta aba: a página está carregando a home agora.
      ultimaTentativa.current.set(perfil, Date.now());
      agendar();
    } else if (visivel() && decorrido() >= RECARGA_VISIVEL_MS) {
      // Aba já carregada há mais de 2 min: ao voltar a ela, atualiza em silêncio.
      void executar('silenciosa');
    } else {
      agendar();
    }

    return () => {
      vivo = false;
      clearTimeout(timer);
      cancelarAssinatura();
      document.removeEventListener('visibilitychange', aoMudarVisibilidade);
    };
  }, [perfilAtivo, assinar]);
}
